import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ClockIcon,
  UserCircleIcon,
  KeyIcon,
  ArrowRightIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../../services/api';

const DemoCredentials = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [credentials, setCredentials] = useState([]);
  const [facilityName, setFacilityName] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const tenantId = searchParams.get('tenant');

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!tenantId) {
        setError('No demo session found. Please start a new demo.');
        setLoading(false);
        return;
      }

      try {
        const portalToken = localStorage.getItem('portalToken') || localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/demo/credentials/${tenantId}`, {
          headers: portalToken ? { 'Authorization': `Bearer ${portalToken}` } : {},
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to fetch credentials');
        }

        const data = await response.json();
        setCredentials(data.credentials || []);
        setFacilityName(data.facility_name || 'Demo Facility');
        setExpiresAt(data.expires_at ? new Date(data.expires_at) : null);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [tenantId]);

  // Update countdown timer
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = new Date();
      const diff = expiresAt - now;

      if (diff <= 0) {
        clearInterval(interval);
        setTimeRemaining('Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      // Fallback for non-secure contexts (plain-HTTP staging)
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      } catch (e) {
        console.error('Failed to copy:', e);
      }
    }
  };

  const handleLogin = (credential) => {
    // Store credentials for the login page auto-fill (Login.js reads 'demoData')
    sessionStorage.setItem('demoData', JSON.stringify({
      facility_name: facilityName,
      expires_in_hours: 24,
      credentials: [{ username: credential.username, password: credential.password, role: credential.role }]
    }));
    sessionStorage.removeItem('demoLogin');
    navigate('/login');
  };

  const getRoleColor = (role) => {
    const colors = {
      ADMIN: 'from-purple-500 to-indigo-600',
      DOCTOR: 'from-teal-500 to-cyan-600',
      NURSE: 'from-pink-500 to-rose-600',
      PHARMACIST: 'from-green-500 to-emerald-600',
      LAB_TECHNICIAN: 'from-blue-500 to-sky-600',
      RECEPTIONIST: 'from-orange-500 to-amber-600',
      CASHIER: 'from-yellow-500 to-lime-600',
    };
    return colors[role] || 'from-gray-500 to-gray-600';
  };

  const getRoleIcon = (role) => {
    const icons = {
      ADMIN: 'A',
      DOCTOR: 'Dr',
      NURSE: 'N',
      PHARMACIST: 'Rx',
      LAB_TECHNICIAN: 'Lab',
      RECEPTIONIST: 'R',
      CASHIER: 'C',
    };
    return icons[role] || '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/30 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Demo Access Error</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/?demo=1')}
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-teal-500/25 transition-all"
          >
            Start New Demo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
            <SparklesIcon className="h-5 w-5 text-teal-400" />
            <span className="text-sm font-medium text-teal-400">Demo Access Granted</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Welcome to {facilityName}
          </h1>
          
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Your demo is ready! You have full admin access to explore the entire system without limits.
          </p>
        </div>

        {/* Countdown Timer */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl">
            <ClockIcon className="h-6 w-6 text-amber-400 animate-pulse" />
            <div>
              <p className="text-xs text-amber-300/70 uppercase tracking-wider">Time Remaining</p>
              <p className="text-2xl font-bold text-white font-mono tabular-nums">{timeRemaining}</p>
            </div>
          </div>
        </div>

        {/* Credentials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {credentials.map((cred, index) => (
            <div
              key={index}
              className="bg-gray-800/50 backdrop-blur-xl rounded-2xl border border-gray-700/50 overflow-hidden hover:border-teal-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/5 group"
            >
              {/* Role Header */}
              <div className={`bg-gradient-to-r ${getRoleColor(cred.role)} p-4`}>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getRoleIcon(cred.role)}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{cred.display_name}</h3>
                    <p className="text-xs text-white/70 uppercase tracking-wider">{cred.role}</p>
                  </div>
                </div>
              </div>
              
              {/* Credentials */}
              <div className="p-4 space-y-3">
                {/* Username */}
                <div className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <UserCircleIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Username</p>
                      <p className="text-sm font-mono text-white">{cred.username}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(cred.username, `user-${index}`)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    {copiedIndex === `user-${index}` ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                
                {/* Password */}
                <div className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <KeyIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Password</p>
                      <p className="text-sm font-mono text-white">{cred.password}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(cred.password, `pass-${index}`)}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    {copiedIndex === `pass-${index}` ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                
                {/* Description */}
                <p className="text-xs text-gray-400 leading-relaxed">{cred.description}</p>
                
                {/* Login Button */}
                <button
                  onClick={() => handleLogin(cred)}
                  className="w-full mt-2 py-2.5 px-4 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/30 text-teal-400 font-medium rounded-xl hover:bg-teal-500/20 hover:border-teal-500/50 transition-all duration-300 flex items-center justify-center gap-2 group-hover:from-teal-500 group-hover:to-cyan-500 group-hover:text-white"
                >
                  Login as {cred.display_name}
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <div className="bg-gray-800/30 backdrop-blur rounded-xl p-6 border border-gray-700/30">
            <ShieldCheckIcon className="h-8 w-8 text-teal-400 mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">Full Admin Access</h3>
            <p className="text-sm text-gray-400">
              You have complete admin access to all modules — patients, clinical, pharmacy, laboratory, billing, and more. No restrictions.
            </p>
          </div>
          
          <div className="bg-gray-800/30 backdrop-blur rounded-xl p-6 border border-gray-700/30">
            <ClockIcon className="h-8 w-8 text-amber-400 mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">7-Day Access</h3>
            <p className="text-sm text-gray-400">
              Your demo expires automatically. Upgrade anytime to keep your data and unlock permanent access.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate('/login')}
            className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-teal-500/25 transition-all duration-300 flex items-center justify-center gap-2"
          >
            Go to Login
            <ArrowRightIcon className="h-5 w-5" />
          </button>
          
          <button
            onClick={() => navigate('/pricing')}
            className="w-full sm:w-auto px-8 py-4 bg-gray-800 text-white font-semibold rounded-xl border border-gray-700 hover:bg-gray-700 transition-all flex items-center justify-center gap-2"
          >
            View Pricing Plans
          </button>
        </div>

        {/* Save Credentials Reminder */}
        <div className="mt-10 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
          <p className="text-sm text-amber-300">
            💡 <strong>Tip:</strong> Save these credentials! They will be needed to log in. You can also bookmark this page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemoCredentials;
